import React, { useMemo, useState } from "react";
import axios from "axios";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEnvelope,
  faHeadset,
  faExclamationTriangle,
  faInfoCircle,
  faCheckCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  Col,
  Row,
  Form,
  Card,
  Button,
  Container,
  InputGroup,
  Modal,
} from "@themesberg/react-bootstrap";
import { Link, useNavigate } from "react-router-dom";

import { Routes } from "../../routes";

// Replace this with your actual logo asset
// import BrandLogo from "../../assets/img/brand/brandlogo.jpg";
import BrandLogo from "../../assets/img/brand/Brand_Logo.png";
import { forgotPasswordsendOtp } from "../api";

// const SEND_RESET_OTP_API_URL = "/auth/forgot-password";

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getLoginPath = () => {
  return Routes?.Signin?.path || "/sign-in";
};

const getResetPasswordPath = () => {
  return Routes?.ChangePassword?.path || "/change-password";
};

const getSupportPath = () => {
  return Routes?.Support?.path || "#";
};

const maskEmail = (email) => {
  const value = String(email || "").trim();
  const atIndex = value.indexOf("@");

  if (atIndex <= 1) return value;

  const name = value.slice(0, atIndex);
  const domain = value.slice(atIndex);

  if (name.length <= 2) {
    return `${name[0]}***${domain}`;
  }

  return `${name[0]}${"*".repeat(Math.max(name.length - 2, 3))}${name[name.length - 1]}${domain}`;
};

export default function ForgotPassword() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    loginId: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [activeModal, setActiveModal] = useState(null);
  const [modalMessage, setModalMessage] = useState("");
  const [supportModalOpen, setSupportModalOpen] = useState(false);
  const [maskedEmail, setMaskedEmail] = useState("");

  const trimmedLoginId = useMemo(() => formData.loginId.trim(), [formData.loginId]);

  const openModal = (type, message = "") => {
    setActiveModal(type);
    setModalMessage(message);
  };

  const closeModal = () => {
    setActiveModal(null);
    setModalMessage("");
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setFieldErrors((prev) => ({
      ...prev,
      [name]: "",
    }));
  };

  const validateBeforeSubmit = () => {
    const errors = {};

    if (!trimmedLoginId) {
      errors.loginId = "Registered Email ID is required.";
    } else if (!emailRegex.test(trimmedLoginId)) {
      errors.loginId = "Invalid email address.";
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSendOtp = async (e) => {
    e.preventDefault();

    if (!validateBeforeSubmit()) return;

    try {
      setSubmitting(true);

      const response = await forgotPasswordsendOtp({
        email: trimmedLoginId,
      });

      const data = response?.data || {};

      if (data?.status === "NOT_FOUND") {
        openModal("accountNotFound", "No account exists with the entered Login ID.");
        return;
      }

      if (data?.status === "BLOCKED") {
        openModal("blockedAccount", "Blocked users may be prevented from reset.");
        return;
      }

      const nextMaskedEmail = data?.maskedEmail || maskEmail(trimmedLoginId);
      setMaskedEmail(nextMaskedEmail);

      openModal("resetOtpSent", `Password reset OTP sent to ${nextMaskedEmail}.`);

      navigate(getResetPasswordPath(), {
        state: {
          loginId: trimmedLoginId,
          maskedEmail: nextMaskedEmail,
          otpSessionId: data?.sessionId || "",
          cooldownSeconds: data?.cooldownSeconds || 30,
        },
      });
    } catch (error) {
      const status = error?.response?.status;
      const message =
        error?.response?.data?.message || "Unable to send OTP. Please try again.";

      if (status === 404) {
        openModal("accountNotFound", message);
      } else if (status === 403) {
        openModal("blockedAccount", message);
      } else {
        openModal("unableToSendOtp", message);
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="auth-shell">
      <section className="py-5">
        <Container>
          <Row className="justify-content-center">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="border-0 auth-card">
                <Card.Body className="p-4 p-md-5">
                  <div className="auth-logo-wrap">
                    <img src={BrandLogo} alt="LifeSkill Global" />
                  </div>

                  <div className="text-center mb-4">
                    <h4 className="fw-bold mb-0">Forgot Password - Request OTP</h4>
                  </div>

                  <Form onSubmit={handleSendOtp} noValidate>
                    <Form.Group className="mb-3" controlId="loginId">
                      <Form.Label className="fw-semibold">
                        Registered Email ID <span className="text-danger">*</span>
                      </Form.Label>
                      <InputGroup
                        className={`flex-nowrap ${
                          fieldErrors.loginId ? "invalid-input-group" : ""
                        }`}
                      >
                        <InputGroup.Text className="bg-white">
                          <FontAwesomeIcon icon={faEnvelope} />
                        </InputGroup.Text>
                        <Form.Control
                          autoFocus
                          name="loginId"
                          type="email"
                          placeholder="Enter Registered Email ID"
                          value={formData.loginId}
                          onChange={handleChange}
                          onBlur={() =>
                            setFormData((prev) => ({
                              ...prev,
                              loginId: prev.loginId.trim(),
                            }))
                          }
                          isInvalid={!!fieldErrors.loginId}
                          autoComplete="email"
                          className="py-2 border-start-0"
                          style={{ boxShadow: "none" }}
                        />
                        <Button
                          type="submit"
                          disabled={submitting}
                          style={{
                            minWidth: "120px",
                            ...ACTIVE_BUTTON_STYLE,
                          }}
                        >
                          Send OTP
                        </Button>
                      </InputGroup>
                      <Form.Control.Feedback type="invalid" className="d-block">
                        {fieldErrors.loginId}
                      </Form.Control.Feedback>
                    </Form.Group>

                    {maskedEmail ? (
                      <Form.Group className="mb-3" controlId="maskedEmail">
                        <Form.Label className="fw-semibold">Reset OTP Sent To</Form.Label>
                        <Form.Control
                          type="text"
                          value={maskedEmail}
                          readOnly
                          className="py-2"
                        />
                      </Form.Group>
                    ) : null}

                    <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
                      <Link
                        to={getLoginPath()}
                        className="fw-semibold underline-link"
                        style={{ color: "#1D4E89" }}
                      >
                        Back to Login
                      </Link>
                    </div>

                    {/* <div className="text-center text-muted small">
                      OTP will always be sent to your registered email address.
                    </div> */}
                  </Form>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="justify-content-center mt-4 mt-md-5">
            <Col xs={12} md={10} lg={8} xl={6}>
              <Card className="border-0 shadow-sm rounded-4" style={{ background: "#ffffff" }}>
                <Card.Body className="p-4 p-md-5">
                  <h4 className="fw-bold mb-2">Need help?</h4>
                  <p className="mb-0 text-muted">
                    Contact your administrator if you cannot receive the reset OTP.
                  </p>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      <Modal show={activeModal === "resetOtpSent"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faCheckCircle} className="me-2 text-success" />
            Reset OTP Sent
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Password reset OTP has been sent to your registered email."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            OK
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "accountNotFound"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Account Not Found
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "No account exists with the entered Login ID."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Try Again
          </Button>
          {/* <Button className="act-btn" onClick={() => setSupportModalOpen(true)}>
            Contact Support
          </Button> */}
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "blockedAccount"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faExclamationTriangle} className="me-2 text-danger" />
            Blocked Account
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "This account is blocked and cannot reset password right now."}
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={closeModal}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={activeModal === "unableToSendOtp"} onHide={closeModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2 text-warning" />
            Unable to Send OTP
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {modalMessage || "Technical failure while sending OTP."}
        </Modal.Body>
        <Modal.Footer>
          <Button className="act-btn" onClick={closeModal}>
            Retry
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={supportModalOpen} onHide={() => setSupportModalOpen(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <FontAwesomeIcon icon={faHeadset} className="me-2" />
            Contact Support
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p className="mb-2">Use your support details here.</p>
          <p className="mb-0 text-muted">
            Example: support@yourcompany.com | +91-XXXXXXXXXX
          </p>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={() => setSupportModalOpen(false)}>
            Close
          </Button>
          <Button
            className="act-btn"
            as={Link}
            to={getSupportPath()}
            onClick={() => setSupportModalOpen(false)}
          >
            Open Support
          </Button>
        </Modal.Footer>
      </Modal>
    </main>
  );
}